"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.listClientsQuerySchema = exports.updateClientSchema = exports.createClientSchema = void 0;
exports.checkClientBusinessRules = checkClientBusinessRules;
const zod_1 = require("zod");
const constants_1 = require("../constants");
const enums_1 = require("../enums");
const validators_1 = require("../validators");
const common_schema_1 = require("./common.schema");
const CLIENT_KIND_VALUES = Object.values(enums_1.ClientKind);
function cleanCui(value) {
    return value.trim().toUpperCase().replace(/^RO/, '').replace(/\s+/g, '');
}
// Bare (undefaulted) field definitions, shared by create and update — same
// reasoning as firm.schema.ts's `firmFields` / series.schema.ts's
// `seriesFields`: zod's `.default()` fires on ANY `undefined` input, even a
// field simply absent from a PATCH body, even after `.partial()`. Applying
// `.default()` here and reusing this object for `updateClientSchema.partial()`
// would mean a PATCH `{phone: "X"}` alone silently resets countryCode/
// isVatPayer/isActive back to their create-time defaults on every update.
// Defaults are therefore only layered on in `createClientSchema` below,
// never on the shape update reuses.
const clientFields = {
    companyId: zod_1.z.uuid('companyId invalid.'),
    kind: zod_1.z.enum(CLIENT_KIND_VALUES, 'Tip client invalid.'),
    name: zod_1.z.string().trim().min(1, 'Numele este obligatoriu.'),
    cui: zod_1.z
        .string()
        .trim()
        .transform(cleanCui)
        .optional(),
    isVatPayer: zod_1.z.boolean(),
    regCom: zod_1.z.string().trim().min(1).optional(),
    cnp: zod_1.z.string().trim().optional(),
    addressLine: zod_1.z.string().trim().min(1, 'Adresa este obligatorie.'),
    cityName: zod_1.z.string().trim().min(1, 'Localitatea este obligatorie.'),
    countyCode: zod_1.z.enum(constants_1.RO_COUNTY_CODES, 'Județ invalid.'),
    postalCode: zod_1.z.string().trim().min(1).optional(),
    countryCode: zod_1.z.string().trim().length(2, 'Codul de țară trebuie să aibă 2 caractere.'),
    email: zod_1.z.email('Email invalid.').optional(),
    phone: zod_1.z.string().trim().min(1).optional(),
    contactName: zod_1.z.string().trim().min(1).optional(),
    bankName: zod_1.z.string().trim().min(1).optional(),
    iban: zod_1.z
        .string()
        .trim()
        .transform((value) => value.toUpperCase().replace(/\s+/g, ''))
        .optional(),
    paymentTermDays: zod_1.z.coerce.number().int().min(0).optional(),
    isActive: zod_1.z.boolean(),
};
/**
 * Cross-field business rules for a client, as plain data (no zod
 * dependency) — reused by:
 *  - createClientSchema's superRefine below (full data, always available at
 *    create time: kind/countyCode/cityName are required fields);
 *  - updateClientSchema's superRefine (fast pre-check only, when a payload
 *    happens to carry enough fields together to evaluate — see its
 *    comment);
 *  - ClientsService.update(), the AUTHORITATIVE check: it calls this again
 *    with the payload merged onto the persisted row, because `kind` is
 *    immutable but `cui`/`cnp`/`countyCode`/`cityName` are not, so a PATCH
 *    touching only one of those fields must still be validated against the
 *    client's *resulting* full state, not just what's in that one payload.
 *    An empty string for `cui`/`cnp` is treated the same as "absent" (a
 *    PATCH `{cui: ""}` on a COMPANY client must still fail: COMPANY requires
 *    a non-empty cui, not just "some string was technically present before
 *    the patch").
 */
function checkClientBusinessRules(data) {
    const issues = [];
    const cui = data.cui ? data.cui : undefined;
    const cnp = data.cnp ? data.cnp : undefined;
    if (data.kind === enums_1.ClientKind.COMPANY) {
        if (!cui) {
            issues.push({ field: 'cui', message: 'CUI este obligatoriu pentru clienți persoană juridică.' });
        }
        else if (!(0, validators_1.isValidCui)(cui)) {
            issues.push({ field: 'cui', message: 'CUI invalid.' });
        }
    }
    else if (data.kind === enums_1.ClientKind.INDIVIDUAL) {
        if (cui) {
            issues.push({ field: 'cui', message: 'CUI nu este permis pentru persoane fizice.' });
        }
        if (cnp && !(0, validators_1.isValidCnp)(cnp)) {
            issues.push({ field: 'cnp', message: 'CNP invalid.' });
        }
    }
    if (data.countyCode !== undefined && data.cityName !== undefined && !(0, constants_1.isCountyCityValid)(data.countyCode, data.cityName)) {
        issues.push({ field: 'cityName', message: constants_1.BUCHAREST_SECTOR_MESSAGE });
    }
    if (data.iban && !(0, validators_1.isValidIban)(data.iban)) {
        issues.push({ field: 'iban', message: 'IBAN invalid.' });
    }
    return issues;
}
function addIssues(issues, ctx) {
    for (const issue of issues) {
        ctx.addIssue({ code: 'custom', path: [issue.field], message: issue.message });
    }
}
exports.createClientSchema = zod_1.z
    .object({
    ...clientFields,
    isVatPayer: clientFields.isVatPayer.default(false),
    countryCode: clientFields.countryCode.default('RO'),
    isActive: clientFields.isActive.default(true),
})
    .superRefine((data, ctx) => {
    addIssues(checkClientBusinessRules(data), ctx);
});
// `kind` stays part of the payload shape (so a client can resend the full
// object on PATCH) but is never used to change the persisted kind — the
// service rejects any attempt to actually change it (400 VALIDATION). Built
// from the undefaulted `clientFields` (see comment above) so an omitted
// field stays truly absent instead of reappearing as its create-time
// default. This schema-level check only validates the *format* of whatever
// fields are actually present in THIS payload (it can't evaluate the
// kind-derived required/forbidden rules without knowing the persisted
// kind/cui/cnp) — ClientsService.update() re-runs checkClientBusinessRules()
// against the full merged state as the authoritative gate.
exports.updateClientSchema = zod_1.z
    .object(clientFields)
    .omit({ companyId: true })
    .partial()
    .superRefine((data, ctx) => {
    if (data.cui !== undefined && data.cui !== '' && !(0, validators_1.isValidCui)(data.cui)) {
        ctx.addIssue({ code: 'custom', path: ['cui'], message: 'CUI invalid.' });
    }
    if (data.cnp !== undefined && data.cnp !== '' && !(0, validators_1.isValidCnp)(data.cnp)) {
        ctx.addIssue({ code: 'custom', path: ['cnp'], message: 'CNP invalid.' });
    }
    if (data.countyCode !== undefined && data.cityName !== undefined && !(0, constants_1.isCountyCityValid)(data.countyCode, data.cityName)) {
        ctx.addIssue({ code: 'custom', path: ['cityName'], message: constants_1.BUCHAREST_SECTOR_MESSAGE });
    }
    if (data.iban !== undefined && data.iban !== '' && !(0, validators_1.isValidIban)(data.iban)) {
        ctx.addIssue({ code: 'custom', path: ['iban'], message: 'IBAN invalid.' });
    }
});
exports.listClientsQuerySchema = common_schema_1.paginationQuerySchema.extend({
    companyId: zod_1.z.uuid('companyId este obligatoriu și trebuie să fie un UUID valid.'),
});
